import { Button, Segmented, Slider, message } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { BgMode, BgSession, cleanupSession, openBgSession, pickColor, removeKeyedColor, resetSession, setMode, setTolerance } from './bgremove';
import Marmotte from './Marmotte';
import { TutoLink } from './Tutos';

interface IProps {
	src: string;
	/** session partagée avec le parent (apply / dirty) */
	sessRef: React.MutableRefObject<BgSession | null>;
	/** notifie le parent à chaque changement (chips, tolérance…) pour re-rendre ses boutons */
	onChanged: () => void;
	/** boutons supplémentaires du parent (Valider / Fermer…) rendus sous Annuler/Reset */
	extra?: React.ReactNode;
	/**
	 * Écran simple (assistant « Préparer le logo », Daniel 09.10.2026 : « UX vraiment simple, rien de caché ») :
	 * consigne au-dessus de l'image, pas de zoom ni de réglette, la tolérance se règle par « Il en reste » /
	 * « Trop a disparu ». L'écran expert (popup depuis la carte) garde zoom, réglette et puces.
	 */
	simple?: boolean;
}

/** Corps réutilisable de « Supprimer des couleurs » : canvas + pipette + tolérance + chips. */
const BgRemovePanel: React.FC<IProps> = ({ src, sessRef, onChanged, extra, simple = false }) => {
	// callback ref : le canvas vit dans un portail antd, monté APRÈS le 1er effet
	const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
	const tolTimer = useRef<number | null>(null);
	const [ready, setReady] = useState(false);
	const [tol, setTol] = useState(18);
	const [zoom, setZoom] = useState<string>('fit');
	const [darkPreview, setDarkPreview] = useState(false);
	const [mode, setModeState] = useState<BgMode>('partout');

	useEffect(() => {
		let alive = true;
		setReady(false);
		sessRef.current = null;
		if (!src || !canvasEl) return undefined;
		openBgSession(src, canvasEl)
			.then(s => {
				if (!alive) return;
				sessRef.current = s;
				setMode(s, mode);
				setReady(true);
				onChanged();
			})
			.catch(() => {
				if (alive) message.error(i18next.t('dtf.err-import-generique', { nom: '' }) as string, 4);
			});
		return () => {
			alive = false;
			sessRef.current = null;
			if (tolTimer.current !== null) window.clearTimeout(tolTimer.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src, canvasEl]);

	const sess = sessRef.current;

	const onPointerDown = (e: React.PointerEvent) => {
		const c = canvasEl;
		if (!c || !sess) return;
		const r = c.getBoundingClientRect();
		if (!r.width || !r.height) return;
		const x = ((e.clientX - r.left) * c.width) / r.width;
		const y = ((e.clientY - r.top) * c.height) / r.height;
		const col = pickColor(sess, x, y);
		if (!col) message.info(i18next.t('dtf.detourage-transparent') as string, 3);
		if (simple) cleanupSession(sess);
		onChanged();
	};

	// tolérance : maj immédiate de l'affichage, recalcul débouncé (image lourde)
	const onTol = (v: number) => {
		setTol(v);
		if (tolTimer.current !== null) window.clearTimeout(tolTimer.current);
		tolTimer.current = window.setTimeout(() => {
			if (sessRef.current) {
				setTolerance(sessRef.current, v);
				if (simple) cleanupSession(sessRef.current);
				onChanged();
			}
		}, 120);
	};
	// écran simple : la tolérance avance par paliers, sans jamais prononcer le mot
	const tolStep = (delta: number) => {
		const v = Math.max(0, Math.min(80, tol + delta));
		if (v === tol) {
			message.info(i18next.t(delta > 0 ? 'dtf.prep-fond-max' : 'dtf.prep-fond-min') as string, 3);
			return;
		}
		setTol(v);
		if (sessRef.current) {
			setTolerance(sessRef.current, v);
			cleanupSession(sessRef.current);
			onChanged();
		}
	};

	const changeMode = (m: BgMode) => {
		setModeState(m);
		if (sessRef.current) setMode(sessRef.current, m);
	};

	const canvasStyle: React.CSSProperties =
		simple || zoom === 'fit'
			? { maxWidth: '100%', maxHeight: '100%' }
			: sess
			? { width: `${Math.round((sess.w * Number(zoom)) / 100)}px`, maxWidth: 'none' }
			: {};

	const keyed = sess ? sess.keyed : [];

	const undo = () => {
		if (sess && sess.keyed.length) {
			removeKeyedColor(sess, sess.keyed.length - 1);
			if (simple) cleanupSession(sess);
			onChanged();
		} else if (simple) message.info(i18next.t('dtf.prep-fond-rien') as string, 3);
	};
	const reset = () => {
		if (sess && sess.keyed.length) {
			resetSession(sess);
			onChanged();
		} else if (simple) message.info(i18next.t('dtf.prep-fond-rien') as string, 3);
	};

	const chips = (
		<div className="dtf-bg-chips">
			{!keyed.length && <span className="dtf-bg-chips-vide">{i18next.t('dtf.detourage-aucune-couleur')}</span>}
			{keyed.map((c, idx) => (
				<button
					// eslint-disable-next-line react/no-array-index-key
					key={`${c.r}-${c.g}-${c.b}-${idx}`}
					type="button"
					className={`dtf-bg-chip${c.seed ? ' dtf-bg-chip-zone' : ''}`}
					title={`${c.seed ? i18next.t('dtf.detourage-mode-zone') + ' · ' : ''}${i18next.t('dtf.detourage-chip-retirer')}`}
					onClick={() => {
						if (sess) {
							removeKeyedColor(sess, idx);
							if (simple) cleanupSession(sess);
							onChanged();
						}
					}}
				>
					<span className="dtf-bg-chip-swatch" style={{ background: `rgb(${c.r},${c.g},${c.b})` }} />
					{c.seed && <span className="dtf-bg-chip-seed" aria-hidden="true">⌖</span>}×
				</button>
			))}
		</div>
	);

	const bgToggle = (
		<div className="dtf-board-bg-toggle" title={i18next.t('dtf.fond-titre') as string}>
			<span className="dtf-bg-toggle-label">{i18next.t(simple ? 'dtf.prep-fond-derriere' : 'dtf.fond-label')}</span>
			<button
				type="button"
				className={`dtf-bg-swatch dtf-bg-swatch-clair${!darkPreview ? ' dtf-bg-swatch-on' : ''}`}
				onClick={() => setDarkPreview(false)}
			>
				{i18next.t(simple ? 'dtf.apercu-clair' : 'dtf.fond-clair')}
			</button>
			<button
				type="button"
				className={`dtf-bg-swatch dtf-bg-swatch-sombre${darkPreview ? ' dtf-bg-swatch-on' : ''}`}
				onClick={() => setDarkPreview(true)}
			>
				{i18next.t(simple ? 'dtf.apercu-sombre' : 'dtf.fond-sombre')}
			</button>
		</div>
	);

	if (simple) {
		return (
			<div className="dtf-bg-simple" data-testid="bg-simple">
				{/* Daniel 09.10 : TOUT est toujours affiché (réglette, paliers, annuler, recommencer) ; sur ordinateur, l'image à gauche
				    et les commandes à droite pour que rien ne passe sous le pli — sur téléphone, une seule colonne qui défile */}
				<div className="dtf-bg-area-left">
				<div className={`dtf-bg-viewer dtf-bg-touch-target dtf-bg-area-viewer${darkPreview ? ' dtf-bg-dark' : ''}`}>
					<canvas ref={setCanvasEl} className="dtf-bg-canvas" style={canvasStyle} onPointerDown={onPointerDown} aria-label={i18next.t('dtf.prep-fond-touche') as string} />
					{!ready && <div className="dtf-bg-loading">{i18next.t('dtf.detourage-chargement')}</div>}
				</div>
				<div className="dtf-bg-area-toggle">{bgToggle}</div>
				</div>
				<div className="dtf-bg-area-right">
				<div className="dtf-bg-mode-simple dtf-bg-area-mode" role="radiogroup" aria-label={i18next.t('dtf.detourage-mode') as string} data-testid="bg-mode-simple">
					<span className="dtf-bg-step-label">{i18next.t('dtf.prep-fond-etape-1')}</span>
					<button type="button" role="radio" aria-checked={mode === 'partout'} className={`dtf-bg-mode-btn${mode === 'partout' ? ' is-on' : ''}`} data-val="partout" onClick={() => changeMode('partout')}>
						{i18next.t('dtf.detourage-mode-partout-long')}
					</button>
					<button type="button" role="radio" aria-checked={mode === 'zone'} className={`dtf-bg-mode-btn${mode === 'zone' ? ' is-on' : ''}`} data-val="zone" onClick={() => changeMode('zone')}>
						{i18next.t('dtf.detourage-mode-zone-long')}
					</button>
				</div>
				<p className="dtf-action-instruction dtf-bg-area-instr" role="status">
					<span className="dtf-bg-step-label">{i18next.t('dtf.prep-fond-etape-2')}</span>
					{i18next.t(mode === 'zone' ? 'dtf.prep-fond-touche-zone' : 'dtf.prep-fond-touche')}
				</p>
				<p className="dtf-prep-hint dtf-bg-mode-aide dtf-bg-area-aide">{i18next.t('dtf.prep-fond-mode-aide')}</p>
				<div className="dtf-bg-area-mmt">
					{keyed.length > 0 ? (
						<Marmotte pose="thumbs" tone="ok" size={48} title={i18next.t('dtf.mmt-couleur-ok-t') as string}>
							{i18next.t('dtf.prep-fond-resultat', { n: keyed.length })} {i18next.t('dtf.prep-fond-nettoye')}
						</Marmotte>
					) : (
						<Marmotte pose="scissors" tone="info" size={48} title={i18next.t('dtf.mmt-couleurs-t') as string}>
							{i18next.t('dtf.mmt-couleurs')}
							<TutoLink n={3} />
							<TutoLink n={4} />
						</Marmotte>
					)}
				</div>
				<div className="dtf-bg-result dtf-bg-area-result" aria-live="polite" data-testid="bg-result">
					{chips}
					<div className="dtf-bg-slider-row dtf-bg-slider-simple">
						<span>{i18next.t('dtf.prep-fond-tolerance')}</span>
						<Slider min={0} max={80} value={tol} onChange={onTol} className="dtf-bg-tol-slider" />
						<span className="dtf-bg-slider-val">{tol}</span>
					</div>
					<div className="dtf-choice-stack">
						<Button size="large" className="dtf-bg-more" onClick={() => tolStep(12)}>
							➕ {i18next.t('dtf.prep-fond-reste')}
						</Button>
						<Button size="large" className="dtf-bg-less" onClick={() => tolStep(-8)}>
							➖ {i18next.t('dtf.prep-fond-trop')}
						</Button>
						<Button size="large" className="dtf-bg-undo" onClick={undo}>
							↩ {i18next.t('dtf.prep-fond-undo')}
						</Button>
						<Button size="large" className="dtf-bg-reset" onClick={reset}>
							⟲ {i18next.t('dtf.detourage-reset')}
						</Button>
					</div>
				</div>
				</div>
				{extra ? <div className="dtf-bg-area-extra">{extra}</div> : null}
			</div>
		);
	}

	return (
		<div className="dtf-bg-body">
			<div className="dtf-bg-viewer-col">
				<div className={`dtf-bg-viewer${darkPreview ? ' dtf-bg-dark' : ''}`}>
					<canvas ref={setCanvasEl} className="dtf-bg-canvas" style={canvasStyle} onPointerDown={onPointerDown} />
					{!ready && <div className="dtf-bg-loading">{i18next.t('dtf.detourage-chargement')}</div>}
				</div>
				<div className="dtf-bg-viewer-tools">
					<Segmented
						size="small"
						value={zoom}
						onChange={v => setZoom(String(v))}
						options={[
							{ value: 'fit', label: i18next.t('dtf.detourage-ajuste') as string },
							{ value: '50', label: '50 %' },
							{ value: '100', label: '100 %' },
							{ value: '200', label: '200 %' },
						]}
					/>
					{bgToggle}
				</div>
			</div>
			<div className="dtf-bg-controls">
				<div className="dtf-bg-pane">
					{/* marmotte vivante : conseil « allégez votre DTF » puis bravo dès qu'une couleur saute */}
					<Marmotte
						key={keyed.length ? 'ok' : 'info'}
						pose={keyed.length ? 'thumbs' : 'scissors'}
						tone={keyed.length ? 'ok' : 'info'}
						size={52}
						title={i18next.t(keyed.length ? 'dtf.mmt-couleur-ok-t' : 'dtf.mmt-couleurs-t') as string}
					>
						{i18next.t(keyed.length ? 'dtf.mmt-couleur-ok' : 'dtf.mmt-couleurs', { n: keyed.length })}
					</Marmotte>
					<div className="dtf-bg-mode-row" data-testid="bg-mode">
						<span>{i18next.t('dtf.detourage-mode')}</span>
						<Segmented
							size="small"
							value={mode}
							onChange={v => changeMode(String(v) as BgMode)}
							options={[
								{ value: 'partout', label: i18next.t('dtf.detourage-mode-partout') as string },
								{ value: 'zone', label: i18next.t('dtf.detourage-mode-zone') as string },
							]}
						/>
					</div>
					<div className="dtf-bg-hint dtf-bg-pipette-aide">{i18next.t(mode === 'zone' ? 'dtf.detourage-pipette-aide-zone' : 'dtf.detourage-pipette-aide')}</div>
					<div className="dtf-bg-slider-row">
						<span>{i18next.t('dtf.detourage-tolerance')}</span>
						<Slider min={0} max={80} value={tol} onChange={onTol} className="dtf-bg-tol-slider" />
						<span className="dtf-bg-slider-val">{tol}</span>
					</div>
					<div className="dtf-bg-hint">{i18next.t('dtf.detourage-tolerance-aide')}</div>
					<div className="dtf-bg-chips-label">{i18next.t('dtf.detourage-couleurs')}</div>
					{chips}
				</div>
				<div className="dtf-bg-common">
					<Button block className="dtf-bg-undo" disabled={!sess || !keyed.length} onClick={undo}>
						↩ {i18next.t('dtf.detourage-undo')}
					</Button>
					<Button block className="dtf-bg-reset" disabled={!sess || !keyed.length} onClick={reset}>
						⟲ {i18next.t('dtf.detourage-reset')}
					</Button>
					{extra}
				</div>
			</div>
		</div>
	);
};

export default BgRemovePanel;
